//mushroom-recipes-page.tsx

import Button from "@mui/material/Button";
import React, { useEffect, useState, useContext } from "react";
import {
  GridColDef,
  GridRowSelectionModel,
  GridPaginationModel,
  GridToolbar,
  GridRowParams,
  GridRowClassNameParams,
  useGridApiRef,
  GridDensity,
  GridInitialState,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridRowId,
} from "@mui/x-data-grid";
import {
  IconButton,
  CircularProgress,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import LoadingBackdrop from "../components/loading-backdrop";
import { UserContext } from "../components/app-routes";
import Page from "../components/page";
import { StripedDataGrid } from "../components/striped-data-grid";
import { DateTime } from "luxon";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { ActiveFilterControls } from "../components/active-filter-controls";
import { getSearchParamNumber } from "../utils/history";
import { useHistory } from "react-router-dom";

type MushroomRecipeType = "culture" | "spawn" | "substrate";

interface MushroomRecipe {
  id: number;
  name: string;
  type: MushroomRecipeType;
  notes: string | null;
  archivedAt: string | null;
}

const recipesGridStateStorageKey = "mushroomRecipesGridState";
const recipesGridDensityStorageKey = "mushroomRecipesGridDensity";

const typeLabel = (t: MushroomRecipeType) => {
  switch (t) {
    case "culture":
      return "Culture";
    case "spawn":
      return "Spawn";
    case "substrate":
      return "Substrate";
    default:
      return t;
  }
};

const RecipesDataGrid = React.memo(
  (props: {
    recipes: MushroomRecipe[] | null;
    onSelectionChange: (params: GridRowSelectionModel) => void;
    initialPage?: number;
    onPaginationModelChange: (m: GridPaginationModel) => void;
    onClickRow: (id: GridRowId) => void;
  }) => {
    const context = useContext(UserContext);
    const grower = context.grower;

    const csvFileName = React.useMemo(() => {
      const zone =
        (grower as any)?.TimeZone ||
        (grower as any)?.timeZone ||
        DateTime.local().zoneName;

      const now = DateTime.local().setZone(zone);

      return `SeedLeaf Mushroom Recipes - ${now.toFormat("MMM dd, yyyy")}`;
    }, [grower]);

    const apiRef = useGridApiRef();
    const [initialState, setInitialState] = React.useState<GridInitialState>();

    const [paginationModel, setPaginationModel] =
      React.useState<GridPaginationModel>(() => {
        const raw = localStorage.getItem(recipesGridStateStorageKey);
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            const saved = parsed?.pagination?.paginationModel;
            if (saved?.pageSize) {
              return {
                page: props.initialPage ?? saved.page ?? 0,
                pageSize: saved.pageSize,
              };
            }
          } catch {}
        }
        return {
          page: props.initialPage ?? 0,
          pageSize: 15,
        };
      });

    const saveSnapshot = React.useCallback(() => {
      if (apiRef?.current?.exportState) {
        const currentState = apiRef.current.exportState();

        if (!(currentState as any)?.pagination?.paginationModel?.pageSize) {
          (currentState as any).pagination = {
            paginationModel: { page: 0, pageSize: 25 },
          };
        }

        localStorage.setItem(
          recipesGridStateStorageKey,
          JSON.stringify(currentState),
        );
      }
    }, [apiRef]);

    React.useLayoutEffect(() => {
      const raw = localStorage?.getItem(recipesGridStateStorageKey);
      let parsed: GridInitialState | undefined;

      if (raw) {
        try {
          parsed = JSON.parse(raw);
        } catch {
          localStorage.removeItem(recipesGridStateStorageKey);
        }
      }

      setInitialState({
        ...parsed,
        columns: {
          columnVisibilityModel: {
            notes: false,
            ...(parsed?.columns?.columnVisibilityModel ?? {}),
          },
          orderedFields: [],
        },
        filter: undefined,
        sorting: parsed?.sorting ?? {
          sortModel: [{ field: "name", sort: "asc" }],
        },
        pagination: {
          paginationModel: {
            page:
              (parsed as any)?.pagination?.paginationModel?.page ??
              props.initialPage ??
              0,
            pageSize:
              (parsed as any)?.pagination?.paginationModel?.pageSize ?? 15,
          },
        },
      });

      window.addEventListener("beforeunload", saveSnapshot);

      return () => {
        window.removeEventListener("beforeunload", saveSnapshot);
        saveSnapshot();
      };
    }, [saveSnapshot, props.initialPage]);

    const [density, setDensity] = React.useState<GridDensity>(() => {
      const d = localStorage.getItem(
        recipesGridDensityStorageKey,
      ) as GridDensity | null;
      return d === "compact" || d === "comfortable" || d === "standard"
        ? d
        : "standard";
    });

    const columns: GridColDef[] = [
      {
        ...GRID_CHECKBOX_SELECTION_COL_DEF,
        hideable: false,
        headerName: "Checkbox",
      },
      {
        field: "archived",
        headerName: "",
        width: 35,
        minWidth: 35,
        sortable: false,
        filterable: false,
        align: "center",
        disableColumnMenu: true,
        disableExport: true,
        renderCell: (params) =>
          params.row.archivedAt ? (
            <Inventory2OutlinedIcon fontSize="small" titleAccess="Archived" />
          ) : null,
      },
      {
        field: "name",
        headerName: "Name",
        width: 280,
      },
      {
        field: "type",
        headerName: "Type",
        width: 160,
        renderCell: (params) => typeLabel(params.row.type),
      },
      {
        field: "notes",
        headerName: "Notes",
        width: 360,
      },
    ];

    if (!props.recipes) return null;

    if (!initialState) {
      return <CircularProgress />;
    }

    return (
      <StripedDataGrid
        apiRef={apiRef}
        columns={columns}
        rows={props.recipes.map((r) => ({
          id: r.id,
          name: r.name,
          type: r.type,
          notes: r.notes,
          archivedAt: r.archivedAt,
        }))}
        initialState={{
          ...initialState,
          filter: undefined,
          sorting: {
            sortModel: [
              {
                field: "name",
                sort: "asc",
              },
            ],
          },
        }}
        checkboxSelection
        onRowSelectionModelChange={props.onSelectionChange}
        pagination
        paginationMode="client"
        pageSizeOptions={[15, 25, 50, 100]}
        paginationModel={paginationModel}
        onPaginationModelChange={(m) => {
          setPaginationModel(m);
          props.onPaginationModelChange(m);
        }}
        showToolbar
        slots={{
          toolbar: GridToolbar,
        }}
        density={density}
        onDensityChange={(d) => {
          setDensity(d);
          localStorage.setItem(recipesGridDensityStorageKey, d);
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            csvOptions: {
              allColumns: true,
              fileName: csvFileName,
            },
          },
        }}
        getRowClassName={(params: GridRowClassNameParams) => {
          const row = params.row as any;
          if (row.archivedAt) {
            return "archived-row";
          }
          return params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd";
        }}
        disableRowSelectionOnClick
        onRowClick={(params: GridRowParams) => {
          props.onClickRow(params.id);
        }}
      />
    );
  },
);

export default function MushroomRecipesPage() {
  const [recipes, setRecipes] = useState<MushroomRecipe[] | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);

  const showArchivedCheckedStorageKey = "mushroomRecipesShowArchivedChecked";
  const showActiveCheckedStorageKey = "mushroomRecipesShowActiveChecked";
  const [showArchivedChecked, setShowArchivedChecked] = useState(
    window.localStorage.getItem(showArchivedCheckedStorageKey) !==
      false.toString(),
  );
  const [showActiveChecked, setShowActiveChecked] = useState(
    window.localStorage.getItem(showActiveCheckedStorageKey) !==
      false.toString(),
  );

  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState("");
  const [addType, setAddType] = useState<MushroomRecipeType>("substrate");
  const [addNotes, setAddNotes] = useState("");

  const history = useHistory();
  let initialPage = getSearchParamNumber(history, "page", 0);

  useEffect(() => {
    setRecipes([
      {
        id: 1,
        name: "Masters Mix (50/50)",
        type: "substrate",
        notes: null,
        archivedAt: null,
      },
      {
        id: 2,
        name: "Grain Spawn (standard)",
        type: "spawn",
        notes: null,
        archivedAt: null,
      },
      {
        id: 3,
        name: "Agar Plate (basic)",
        type: "culture",
        notes: null,
        archivedAt: null,
      },
    ]);
  }, []);

  const handleSelectionChange = (m: GridRowSelectionModel) => {
    let ids: number[] = [];
    const anyM = m as any;
    if (Array.isArray(m)) ids = m.map((v) => Number(v));
    else if (anyM?.ids?.forEach) {
      const t: number[] = [];
      anyM.ids.forEach((v: unknown) => t.push(Number(v)));
      ids = t;
    }
    setSelected(ids.length ? [ids[0]] : []);
  };

  const handleShowActiveChanged = (checked: boolean) => {
    setShowActiveChecked(checked);
    window.localStorage.setItem(
      showActiveCheckedStorageKey,
      checked.toString(),
    );
  };

  const handleShowArchivedChanged = (checked: boolean) => {
    setShowArchivedChecked(checked);
    window.localStorage.setItem(
      showArchivedCheckedStorageKey,
      checked.toString(),
    );
  };

  const onClickRow = (id: GridRowId) => {
    // Placeholder: later we can route to /mushroom/home/recipes/:id
    return;
  };

  const handleClickAdd = () => {
    setAddName("");
    setAddType("substrate");
    setAddNotes("");
    setAddOpen(true);
  };

  const handleCloseAdd = () => {
    setAddOpen(false);
  };

  const handleSaveAdd = async () => {
    if (!recipes) return;

    if (!addName.trim()) {
      return;
    }

    setBusy(true);

    try {
      const maxId = recipes.reduce((m, r) => Math.max(m, r.id), 0);

      const next: MushroomRecipe = {
        id: maxId + 1,
        name: addName.trim(),
        type: addType,
        notes: addNotes.trim() ? addNotes.trim() : null,
        archivedAt: null,
      };

      setRecipes([next, ...recipes]);
      setAddOpen(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page title="Recipes">
      {recipes && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 2,
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleClickAdd}
              disabled={busy}
            >
              Add
            </Button>

            <IconButton
              aria-label="delete"
              disabled={busy || selected?.length === 0}
              onClick={() => {
                return;
              }}
              size="large"
            >
              <DeleteIcon />
            </IconButton>

            <ActiveFilterControls
              showActiveChecked={showActiveChecked}
              showArchivedChecked={showArchivedChecked}
              onChangeShowActive={handleShowActiveChanged}
              onChangeShowArchived={handleShowArchivedChanged}
            />
          </Box>

          <Box sx={{ height: 750, width: "100%", pt: 2 }}>
            <RecipesDataGrid
              recipes={recipes.filter(
                (recipe) =>
                  (showActiveChecked && recipe.archivedAt === null) ||
                  (showArchivedChecked && recipe.archivedAt !== null),
              )}
              onSelectionChange={handleSelectionChange}
              initialPage={initialPage}
              onPaginationModelChange={(m) => {
                const params = new URLSearchParams(`page=${m.page}`);
                history.replace({
                  search: params.toString(),
                });
              }}
              onClickRow={onClickRow}
            />
          </Box>

          <Dialog
            open={addOpen}
            onClose={handleCloseAdd}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>Add Recipe</DialogTitle>
            <DialogContent>
              <Box
                sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 2 }}
              >
                <TextField
                  label="Name"
                  value={addName}
                  onChange={(e) => setAddName(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Type"
                  value={addType}
                  onChange={(e) =>
                    setAddType(e.target.value as MushroomRecipeType)
                  }
                  select
                  fullWidth
                >
                  <MenuItem value="culture">Culture</MenuItem>
                  <MenuItem value="spawn">Spawn</MenuItem>
                  <MenuItem value="substrate">Substrate</MenuItem>
                </TextField>
                <TextField
                  label="Notes"
                  value={addNotes}
                  onChange={(e) => setAddNotes(e.target.value)}
                  fullWidth
                  multiline
                  minRows={3}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseAdd} disabled={busy}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveAdd}
                color="primary"
                variant="contained"
                disabled={busy || !addName.trim()}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      <LoadingBackdrop open={busy} />
    </Page>
  );
}
